import { apiService } from '../api';
import {
  AdmissionCycleSchedule,
  ApplicationWindow,
  IntakeChannelState,
  AdmissionFormSection,
  AdmissionFeeHead,
  DocumentChecklistItem,
  EligibilityRule,
  IdNamePair,
  ApplicationStatus,
} from '../../types/admissions';

/**
 * Public, no-auth admission flow. The parent-facing apply page uses these.
 * See backend/src/controllers/admissions/publicApplyController.ts for the
 * server contract.
 */

export interface PublicCycleMetadata {
  _id: string;
  name: string;
  description?: string;
  /** School brand surfaced in the public form hero. */
  school?: { name: string; logo?: string | null };
  status: string;
  /** Where the online form stands today, and why it isn't open (or null). */
  onlineState: IntakeChannelState;
  closedReason: string | null;
  schedule: Pick<AdmissionCycleSchedule,
    'applicationOpen' | 'applicationClose' | 'admissionFeeDeadline' | 'classesBeginOn'>;
  /** The application window on the school's calendar (its timezone), as the
   *  server sees it — the page shows this, not the visitor's own clock. */
  applicationWindow: ApplicationWindow;
  allowedClassIds: IdNamePair[];
  formTemplate: { sections: AdmissionFormSection[] };
  applicationFeeAmount: number;
  admissionFeeHeads: AdmissionFeeHead[];
  /** Documents asked for, per group of classes — the form shows the chosen
   *  class's list (pathwayFor). */
  pathways: Array<{ key: string; classIds: string[]; documentChecklist: DocumentChecklistItem[] }>;
  /** Age limits per class — the form shows the child's age against them. */
  eligibility: Pick<EligibilityRule, 'classId' | 'ageOn'>[];
  prospectusFilename?: string | null;
}

export interface SubmitPublicApplicationPayload {
  name: string;
  fatherName: string;
  fatherPhone: string;
  address: string;
  cnic: string;
  gender: 'male' | 'female' | 'other';
  classId: string;
  candidateContact?: string;
  email?: string;
  dob?: string;
  previousSchool?: string;
  previousPercentage?: number;
  previousSubjects?: string[];
  // Guardian capture — stored as pendingGuardians[] until staff Resolve Family.
  fatherCnic?: string;
  fatherEmail?: string;
  fatherOccupation?: string;
  motherName?: string;
  motherCnic?: string;
  motherPhone?: string;
  motherEmail?: string;
  motherOccupation?: string;
  // Dynamic form field responses — keyed by cycle.formTemplate field keys.
  formData?: Record<string, any>;
}

export interface SubmitPublicApplicationResponse {
  applicationId: string;
  applicationNumber: string;
  statusToken: string;
  status: ApplicationStatus;
}

/** What a form link shows (mirror of backend applicationInvites.invitePage). */
export type PublicFormLink =
  | {
    state: 'open';
    cycle: PublicCycleMetadata;
    /** What the desk confirmed: locked on the form, except the email and class. */
    prefill: { cnic: string; fatherCnic: string | null; name: string | null; email: string; classId: string | null; returning: boolean };
  }
  | { state: 'submitted'; statusToken: string | null; applicationNumber: string | null }
  | { state: 'cancelled'; school: { name: string; logo?: string | null } }
  | { state: 'closed'; admission: string | null; school: { name: string; logo?: string | null } };

export const publicApplyService = {
  getCycle: async (token: string): Promise<PublicCycleMetadata> => {
    const r = await apiService.get<PublicCycleMetadata>(`/public/apply/${token}`);
    return r.data!;
  },

  /** A family's own form link (/form/:token): the form to fill in, or where things stand. */
  getFormLink: async (token: string): Promise<PublicFormLink> =>
    (await apiService.get<PublicFormLink>(`/public/form-link/${token}`)).data!,

  submitFormLink: async (token: string, payload: SubmitPublicApplicationPayload): Promise<SubmitPublicApplicationResponse> =>
    (await apiService.post<SubmitPublicApplicationResponse>(`/public/form-link/${token}`, payload)).data!,

  submit: async (token: string, payload: SubmitPublicApplicationPayload): Promise<SubmitPublicApplicationResponse> => {
    const r = await apiService.post<SubmitPublicApplicationResponse>(`/public/apply/${token}`, payload);
    return r.data!;
  },
};
